<template>
  <!-- The periscope install guide (docs/tutorial-periscopio.md): a full screen, the stage on the left (an
       animated corner of the tablet, or the live photo of the camera) and one step's words on the right. -->
  <section
    class="pt"
    data-testid="periscope-tutorial"
    :data-step="step"
    :style="motionVars"
    :aria-labelledby="titleId"
  >
    <header class="pt-bar">
      <BzButton
        class="pt-exit"
        kind="secondary"
        size="md"
        icon="close"
        :label="$t('message.periscopeTutorialExit')"
        data-testid="periscope-exit"
        @click="exit"
      />
      <span class="pt-bar-title">{{ $t('message.periscopeTutorialTitle') }}</span>
      <div v-if="stepNumber" class="pt-progress" data-testid="periscope-progress">
        <span class="pt-progress-text">{{ $t('message.periscopeTutorialStepOf', { step: stepNumber, total: totalSteps }) }}</span>
        <span class="pt-dots" aria-hidden="true">
          <i v-for="n in totalSteps" :key="n" class="pt-dot" :class="{ 'is-done': n <= stepNumber }"></i>
        </span>
      </div>
    </header>

    <div class="pt-main">
      <div ref="stage" class="pt-stage">
        <transition name="pt-fade" mode="out-in">
          <!-- NEEDS: what to find before starting. -->
          <div v-if="step === 'needs'" key="needs" class="pt-needs">
            <figure v-for="item in needs" :key="item.id" class="pt-need">
              <img class="pt-need-img" :src="item.src" alt="" aria-hidden="true" draggable="false" />
              <figcaption class="pt-need-caption">{{ $t(item.label) }}</figcaption>
            </figure>
          </div>

          <!-- ALIGN and CHECK: the real photo of the camera. One element for both, so going from one to the
               other (or back) keeps the camera open. -->
          <div v-else-if="isLive" key="live" class="pt-live-wrap">
            <div
              class="pt-live"
              :class="{ 'has-photo': !!liveUrl }"
              data-testid="periscope-live"
              :data-state="liveState"
              :data-source="liveSource"
            >
              <CameraPreviewComponent
                :key="cameraKey"
                ref="camera"
                class="pt-camera"
                :settings="cameraSettings"
                :selected-camera-id="settings.selectedCameraId"
                :full-frame="true"
                @camera-state="onCameraState"
              />
              <!-- Two buffers: a new photo loads and decodes in the one behind, and only then does it come to the
                   front. The one that was in front stays up until that moment, so there is never a bare frame. -->
              <template v-for="(url, i) in photos" :key="i">
                <img
                  v-if="url"
                  class="pt-photo"
                  :class="{ 'is-front': i === front }"
                  :src="url"
                  :alt="i === front ? $t('message.periscopeTutorialLiveAlt') : ''"
                  :aria-hidden="i === front ? undefined : 'true'"
                  :style="photoFlip"
                  draggable="false"
                  @load="onPhotoLoad($event, i)"
                  @error="onPhotoError(i)"
                />
              </template>
              <span v-if="liveState === 'live'" class="pt-live-badge">
                <i class="pt-live-dot" aria-hidden="true"></i>
                {{ $t('message.periscopeTutorialLiveBadge') }}
              </span>
              <div v-if="liveState === 'starting'" class="pt-live-msg" role="status" aria-live="polite">
                <BzIcon name="progress_activity" class="pt-live-icon bz-spinner" />
                <p class="pt-live-text">{{ $t('message.periscopeTutorialLiveStarting') }}</p>
              </div>
              <div v-else-if="liveState === 'error'" class="pt-live-msg" role="alert">
                <BzIcon name="error" class="pt-live-icon" />
                <p class="pt-live-text">{{ $t('message.periscopeTutorialLiveError') }}</p>
                <BzButton
                  kind="secondary"
                  size="lg"
                  icon="refresh"
                  :label="$t('message.periscopeTutorialLiveRetry')"
                  data-testid="periscope-live-retry"
                  @click="retryLive"
                />
              </div>
            </div>
            <ul v-if="step === 'align'" class="pt-examples" :style="{ '--pt-examples': examples.length }">
              <li v-for="ex in examples" :key="ex.id" class="pt-example">
                <div class="pt-example-pic">
                  <img class="pt-example-img" :src="ex.src" alt="" aria-hidden="true" draggable="false" />
                  <span v-if="ex.band" class="pt-band" :class="`pt-band--${ex.band}`" aria-hidden="true"></span>
                </div>
                <p class="pt-example-caption">
                  <BzIcon :name="ex.ok ? 'check' : 'close'" class="pt-example-icon" :class="ex.ok ? 'is-ok' : 'is-wrong'" />
                  {{ $t(ex.label) }}
                </p>
              </li>
            </ul>
          </div>

          <!-- Every other step: an illustrated scene on a 760x640 stage that covers the box. -->
          <div v-else :key="step" class="pt-holder">
            <!-- Re-keying this element is what "Ver otra vez" does: a new element starts its animations again. -->
            <div :key="replayKey" class="pt-scene" :class="`pt-scene--${step}`" :style="sceneStyle">
              <div v-if="framed" class="pt-frame" :style="frameStyle">
                <template v-if="step === 'welcome' || step === 'done'">
                  <img class="pt-layer" :src="layers.cornerTablet.src" :style="box('cornerTablet')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer" :src="layers.cornerPeriShadow.src" :style="box('cornerPeriShadow')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer" :src="layers.cornerPeri.src" :style="box('cornerPeri')" alt="" aria-hidden="true" draggable="false" />
                </template>

                <template v-else-if="step === 'clean'">
                  <img class="pt-layer" :src="layers.cornerTablet.src" :style="box('cornerTablet')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-cloth" :src="layers.cornerCloth.src" :style="box('cornerCloth')" alt="" aria-hidden="true" draggable="false" />
                </template>

                <template v-else-if="step === 'place'">
                  <img class="pt-layer" :src="layers.cornerTablet.src" :style="box('cornerTablet')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-shade-drop" :src="layers.cornerPeriShadow.src" :style="box('cornerPeriShadow')" alt="" aria-hidden="true" draggable="false" />
                  <div class="pt-drop">
                    <img class="pt-layer" :src="layers.cornerPeri.src" :style="box('cornerPeri')" alt="" aria-hidden="true" draggable="false" />
                  </div>
                </template>

                <template v-else-if="step === 'mark'">
                  <img class="pt-layer" :src="layers.cornerTablet.src" :style="box('cornerTablet')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-xray" :src="layers.cornerPeriShadow.src" :style="box('cornerPeriShadow')" alt="" aria-hidden="true" draggable="false" />
                  <img
                    v-for="(k, i) in markKeys"
                    :key="k"
                    class="pt-layer pt-mark"
                    :class="`pt-mark--${i}`"
                    :src="layers[k].src"
                    :style="box(k)"
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                  <!-- over the marks, half-transparent while the pencil goes round (x-ray): the line shows through it -->
                  <img class="pt-layer pt-xray" :src="layers.cornerPeri.src" :style="box('cornerPeri')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-pencil" :src="layers.cornerPencil.src" :style="box('cornerPencil')" alt="" aria-hidden="true" draggable="false" />
                </template>

                <template v-else-if="step === 'stick'">
                  <img class="pt-layer" :src="layers.cornerTablet.src" :style="box('cornerTablet')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-shade-press" :src="layers.cornerPeriShadow.src" :style="box('cornerPeriShadow')" alt="" aria-hidden="true" draggable="false" />
                  <img v-for="k in markKeys" :key="k" class="pt-layer" :src="layers[k].src" :style="box(k)" alt="" aria-hidden="true" draggable="false" />
                  <!-- The red liner is only there the first time: after unsticking, the periscope is put back as it is. -->
                  <img v-if="!reattach" class="pt-layer pt-liner" :src="layers.cornerLiner.src" :style="box('cornerLiner')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-press" :src="layers.cornerPeri.src" :style="box('cornerPeri')" alt="" aria-hidden="true" draggable="false" />
                  <span class="pt-ripple" :style="pressAt" aria-hidden="true"></span>
                  <span class="pt-tap" :style="pressAt" aria-hidden="true">
                    <BzIcon name="touch_app" class="pt-tap-icon" />
                  </span>
                </template>

                <!-- RETAPE: the periscope lifts, turns over (base up), the new strip flies from the roll onto its base,
                     it turns back and comes down. Its frames (with and without the tape) are shown one at a time. -->
                <template v-else-if="step === 'retape'">
                  <img class="pt-layer" :src="layers.cornerTablet.src" :style="box('cornerTablet')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer" :src="layers.cornerTapeRoll.src" :style="box('cornerTapeRoll')" alt="" aria-hidden="true" draggable="false" />
                  <!-- The finished pose (the periscope down on its tape): what is left when the motion is over. -->
                  <img class="pt-layer pt-retape-rest" :src="layers.cornerPeriShadow.src" :style="box('cornerPeriShadow')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-retape-rest" :src="layers.cornerPeri.src" :style="box('cornerPeri')" alt="" aria-hidden="true" draggable="false" />
                  <div class="pt-flip" :style="flipVars">
                    <img
                      v-for="f in flipFrames"
                      :key="f.id"
                      class="pt-layer pt-flip-frame"
                      :src="f.src"
                      :style="{ ...boxOf(f), '--pt-a': f.at + 'ms', '--pt-d': f.dur + 'ms' }"
                      alt=""
                      aria-hidden="true"
                      draggable="false"
                    />
                  </div>
                  <img class="pt-layer pt-flip-strip" :src="layers.cornerFlipStrip.src" :style="box('cornerFlipStrip')" alt="" aria-hidden="true" draggable="false" />
                </template>

                <!-- UNSTICK: the periscope turns on itself (its frames, one visible at a time), then comes off. -->
                <template v-else-if="step === 'unstick'">
                  <img class="pt-layer" :src="layers.cornerTablet.src" :style="box('cornerTablet')" alt="" aria-hidden="true" draggable="false" />
                  <img class="pt-layer pt-shade-unstick" :src="layers.cornerPeriShadow.src" :style="box('cornerPeriShadow')" alt="" aria-hidden="true" draggable="false" />
                  <div class="pt-turn" :style="turnVars">
                    <img
                      v-for="(f, i) in turnFrames"
                      :key="i"
                      class="pt-layer pt-turn-frame"
                      :class="{ 'is-first': i === 0 }"
                      :src="f.src"
                      :style="{ ...boxOf(f), '--pt-i': i }"
                      alt=""
                      aria-hidden="true"
                      draggable="false"
                    />
                  </div>
                </template>
              </div>

              <!-- Three sparkles pop where the cloth has been. -->
              <template v-if="step === 'clean'">
                <svg
                  v-for="(p, i) in sparkles"
                  :key="i"
                  class="pt-spark"
                  :class="`pt-spark--${i}`"
                  :style="{ left: p[0] + 'px', top: p[1] + 'px' }"
                  viewBox="-10 -10 20 20"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M0 -10 Q1.2 -1.2 10 0 Q1.2 1.2 0 10 Q-1.2 1.2 -10 0 Q-1.2 -1.2 0 -10Z" />
                </svg>
              </template>
            </div>

            <span v-if="step === 'done'" class="pt-ok-badge" aria-hidden="true">
              <BzIcon name="check" class="pt-ok-icon" />
            </span>

            <!-- PLACE: the side view (camera -> mirror -> table), a card over the corner of the stage. -->
            <div v-if="step === 'place'" class="pt-inset" :style="insetStyle">
              <div class="pt-inset-art">
                <div class="pt-section">
                  <div class="pt-section-scene" :style="sectionStyle">
                    <img class="pt-layer" :src="layers.sideTable.src" :style="box('sideTable')" alt="" aria-hidden="true" draggable="false" />
                    <img class="pt-layer" :src="layers.sideTablet.src" :style="box('sideTablet')" alt="" aria-hidden="true" draggable="false" />
                    <img class="pt-layer pt-ghost" :src="layers.sidePeriFull.src" :style="box('sidePeriFull')" alt="" aria-hidden="true" draggable="false" />
                    <img class="pt-layer" :src="layers.sidePeri.src" :style="box('sidePeri')" alt="" aria-hidden="true" draggable="false" />
                    <svg class="pt-ray" :width="stage.width" :height="stage.height" aria-hidden="true" focusable="false">
                      <path :d="rayPath" />
                    </svg>
                  </div>
                </div>
                <span v-for="(c, i) in chips" :key="c.id" class="pt-chip" :style="{ left: c.x + 'px', top: c.y + 'px' }">
                  <b class="pt-chip-n">{{ i + 1 }}</b>
                  {{ $t(c.label) }}
                </span>
              </div>
              <p class="pt-inset-caption">{{ $t('message.periscopeTutorialPlaceInset') }}</p>
            </div>
          </div>
        </transition>
      </div>

      <div class="pt-copy">
        <transition name="pt-step" mode="out-in">
          <div :key="step" class="pt-copy-inner">
            <h1 :id="titleId" class="pt-title">{{ $t(copy.title) }}</h1>
            <p v-for="key in copy.body" :key="key" class="pt-text">{{ $t(key) }}</p>
            <p v-if="copy.strong" class="pt-text pt-strong">{{ $t(copy.strong) }}</p>
            <p v-if="copy.note" class="pt-note">{{ $t(copy.note) }}</p>
            <div class="pt-actions">
              <BzButton
                kind="primary"
                size="xl"
                block
                :label="$t(copy.primary)"
                data-testid="periscope-primary"
                @click="onPrimary"
              />
              <BzButton
                v-if="copy.secondary"
                kind="secondary"
                size="lg"
                block
                :label="$t(copy.secondary)"
                data-testid="periscope-secondary"
                @click="onSecondary"
              />
              <div v-if="canGoBack || canReplay" class="pt-aux">
                <BzButton
                  v-if="canGoBack"
                  kind="text"
                  size="md"
                  :label="$t('message.periscopeTutorialBack')"
                  data-testid="periscope-back"
                  @click="back"
                />
                <BzButton
                  v-if="canReplay"
                  kind="text"
                  size="md"
                  icon="refresh"
                  :label="$t('message.periscopeTutorialReplay')"
                  data-testid="periscope-replay"
                  @click="replay"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * PeriscopeTutorialScreen — the step-by-step guide to placing the periscope (docs/tutorial-periscopio.md).
 *
 * App.vue owns every piece of state: this screen takes `settings`, `deviceId` and where it was opened from
 * (`periscopeSource`), and says two things upward: `update-settings` (it turns on Modo Periscopio™ when the
 * last step is reached) and `periscope-close` with how it ended. What it does remember is only where the
 * person is in the guide.
 *
 * The illustrations are the layers of src/periscopeTutorialArt.js (generated) on a 760x640 stage, scaled by
 * JS to cover the box; their motion is CSS (transform and opacity only), three runs and then the finished
 * pose, and none under reduced motion, where the finished pose is simply what is drawn.
 */
import BzButton from '@/ui/components/BzButton.vue';
import BzIcon from '@/ui/components/BzIcon.vue';
import CameraPreviewComponent from './CameraPreviewComponent.vue';
import { PERISCOPE_TUTORIAL_STEPS, PERISCOPE_OUTCOME, trackPeriscope } from '@/periscopeTutorial';
import { STAGE, LAYERS, MOTION, FRAMING, SECTION, EXAMPLES, TURN_FRAMES, FLIP_FRAMES, FLIP_FRAMES_TAPED } from '@/periscopeTutorialArt';

const TOTAL_STEPS = PERISCOPE_TUTORIAL_STEPS.length;

/** The words of each step: i18n keys only. `body` is a list of paragraphs; `strong` is the sentence to keep. */
const COPY = {
  welcome: {
    title: 'message.periscopeTutorialWelcomeTitle',
    body: ['message.periscopeTutorialWelcomeBody'],
    note: 'message.periscopeTutorialWelcomeNote',
    primary: 'message.periscopeTutorialWelcomeStart',
    secondary: 'message.periscopeTutorialWelcomeAlready',
  },
  needs: {
    title: 'message.periscopeTutorialNeedsTitle',
    body: ['message.periscopeTutorialNeedsBody'],
    primary: 'message.periscopeTutorialNeedsNext',
  },
  clean: {
    title: 'message.periscopeTutorialCleanTitle',
    body: ['message.periscopeTutorialCleanBody'],
    strong: 'message.periscopeTutorialCleanStrong',
    primary: 'message.periscopeTutorialCleanNext',
  },
  place: {
    title: 'message.periscopeTutorialPlaceTitle',
    body: ['message.periscopeTutorialPlaceBody'],
    strong: 'message.periscopeTutorialPlaceStrong',
    primary: 'message.periscopeTutorialPlaceNext',
  },
  align: {
    title: 'message.periscopeTutorialAlignTitle',
    body: ['message.periscopeTutorialAlignBody'],
    strong: 'message.periscopeTutorialAlignStrong',
    primary: 'message.periscopeTutorialAlignNext',
  },
  mark: {
    title: 'message.periscopeTutorialMarkTitle',
    body: ['message.periscopeTutorialMarkBody'],
    primary: 'message.periscopeTutorialMarkNext',
  },
  stick: {
    title: 'message.periscopeTutorialStickTitle',
    body: ['message.periscopeTutorialStickBody'],
    strong: 'message.periscopeTutorialStickStrong',
    primary: 'message.periscopeTutorialStickNext',
  },
  // The stick step after unsticking: the same words are not true any more (there is no plastic to remove).
  restick: {
    title: 'message.periscopeTutorialRestickTitle',
    body: ['message.periscopeTutorialRestickBody'],
    strong: 'message.periscopeTutorialStickStrong',
    primary: 'message.periscopeTutorialStickNext',
    secondary: 'message.periscopeTutorialRestickBad',
  },
  retape: {
    title: 'message.periscopeTutorialRetapeTitle',
    body: ['message.periscopeTutorialRetapeBody'],
    primary: 'message.periscopeTutorialRetapeNext',
  },
  unstick: {
    title: 'message.periscopeTutorialUnstickTitle',
    body: ['message.periscopeTutorialUnstickBody'],
    primary: 'message.periscopeTutorialUnstickNext',
  },
  check: {
    title: 'message.periscopeTutorialCheckTitle',
    body: ['message.periscopeTutorialCheckBody'],
    primary: 'message.periscopeTutorialCheckYes',
    secondary: 'message.periscopeTutorialCheckNo',
  },
  done: {
    title: 'message.periscopeTutorialDoneTitle',
    body: ['message.periscopeTutorialDoneBody', 'message.periscopeTutorialDoneBody2'],
    primary: 'message.periscopeTutorialDoneFinish',
  },
};

/**
 * The branches off the numbered steps, each with the step it hangs off (where Atrás returns to):
 * `unstick` comes from `check` ("no, it has to move") and goes on to `align`; `retape` comes from the
 * re-attach pass of `stick` ("it does not stick well") and goes back to `stick`.
 */
const UNSTICK = 'unstick';
const RETAPE = 'retape';
const BRANCH_PARENT = Object.freeze({ [UNSTICK]: 'check', [RETAPE]: 'stick' });

/** The steps whose animation can be watched again. */
const REPLAYABLE = ['clean', 'place', 'mark', 'stick', UNSTICK, RETAPE];
/** The steps drawn on the corner of the tablet, inside a framed (translated and scaled) group of layers. */
const FRAMED = ['welcome', 'clean', 'place', 'mark', 'stick', UNSTICK, RETAPE, 'done'];
const MARK_KEYS = ['cornerMark0', 'cornerMark1', 'cornerMark2'];
const SPARKLES = [[250, 300], [330, 262], [300, 360]];

// UNSTICK: one turn of the periscope takes 2.4 s; with the lift and a beat at rest, one run is 4.2 s. The
// keyframes of `pt-unstick-lift` and `pt-shade-unstick` hold the lift from the end of the turn (2.4 / 4.2).
const TURN_MS = 2400;
const TURN_CYCLE_MS = 4200;

// RETAPE: one run is 8 s. Where each beat falls, in % of the run (the keyframes `pt-flip-*`, `pt-retape-rest`
// and `pt-flip-strip` below use the same numbers): it rests until 6, lifts to 16, turns over to 38, the strip
// flies in from 40 to 55, the frames swap to the taped ones at 58, it turns back to 80, comes down to 90 and
// hands over to the finished pose.
const FLIP_CYCLE_MS = 8000;
const FLIP = Object.freeze({ turnStart: 16, turnEnd: 38, swap: 58, backEnd: 80, handover: 90 });

// The live view: how many photos in a row may fail before it says so, the pause between tries, and how
// long "opening the camera" may last before it says so too.
const LIVE_MAX_FAILURES = 3;
const LIVE_RETRY_MS = 400;
const LIVE_START_LIMIT_MS = 20000;

/** A translate argument list (`x, y`, in px) that a keyframe can take through `var()`. */
const pair = ([x, y]) => `${x}px, ${y}px`;

/**
 * The motion vectors of the art module as custom properties of the screen, so the keyframes below take
 * their numbers from the module instead of writing them again.
 */
const MOTION_VARS = Object.freeze({
  '--pt-lift40': pair(MOTION.lift40),
  '--pt-lift30': pair(MOTION.lift30),
  '--pt-peel': pair(MOTION.peel),
  '--pt-flip-drop': pair(MOTION.flipDrop),
  '--pt-flip-strip-from': pair(MOTION.flipStripFrom),
  '--pt-wipe-0': pair(MOTION.wipe[0]),
  '--pt-wipe-1': pair(MOTION.wipe[1]),
  '--pt-wipe-2': pair(MOTION.wipe[2]),
  '--pt-wipe-3': pair(MOTION.wipe[3]),
  '--pt-pencil-1': pair(MOTION.pencil[1]),
  '--pt-pencil-2': pair(MOTION.pencil[2]),
  '--pt-pencil-3': pair(MOTION.pencil[3]),
});

const px = (n) => `${n}px`;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default {
  name: 'PeriscopeTutorialScreen',
  components: { BzButton, BzIcon, CameraPreviewComponent },
  inheritAttrs: false,
  props: {
    settings: { type: Object, required: true },
    deviceId: { type: String, default: '' },
    /** Where it was opened from: 'first_login' (with a welcome), 'settings' or 'link' (straight to the guide). */
    periscopeSource: { type: String, default: 'settings' },
  },
  emits: ['update-settings', 'periscope-close'],
  data() {
    return {
      stepIndex: 0,
      // The branch the person is on, if any ('unstick', 'retape'): `stepIndex` stays on the step it hangs off.
      branch: '',
      // Set when the person leaves `unstick`: the next `stick` is a re-attach (the plastic is gone).
      reattach: false,
      replayKey: 0,
      layers: LAYERS,
      turnFrames: TURN_FRAMES,
      stage: STAGE,
      markKeys: MARK_KEYS,
      sparkles: SPARKLES,
      motionVars: MOTION_VARS,
      // The stage's box, measured: what the scene is scaled by.
      stageScale: 1,
      sceneTransform: '',
      // The live view: 'starting' | 'live' | 'error'; where the photos come from; the two photo buffers
      // (object URLs, '' when empty) and which of them is the one on screen.
      liveState: 'starting',
      liveSource: '',
      photos: ['', ''],
      front: 0,
      cameraKey: 0,
      titleId: 'periscope-tutorial-title',
      needs: [
        { id: 'peri', src: LAYERS.needPeri.src, label: 'message.periscopeTutorialNeedsPeriscope' },
        { id: 'bottle', src: LAYERS.needBottle.src, label: 'message.periscopeTutorialNeedsAlcohol' },
        { id: 'cloth', src: LAYERS.needCloth.src, label: 'message.periscopeTutorialNeedsCloth' },
        { id: 'pencil', src: LAYERS.needPencil.src, label: 'message.periscopeTutorialNeedsPencil' },
      ],
    };
  },
  computed: {
    steps() {
      const list = [...PERISCOPE_TUTORIAL_STEPS, 'done'];
      return this.periscopeSource === 'first_login' ? ['welcome', ...list] : list;
    },
    step() {
      return this.branch || this.steps[this.stepIndex];
    },
    copy() {
      return this.step === 'stick' && this.reattach ? COPY.restick : COPY[this.step];
    },
    totalSteps() {
      return TOTAL_STEPS;
    },
    /** "Paso n": 1 to 7 on the numbered steps, 0 on the welcome, the end and the branches. */
    stepNumber() {
      return PERISCOPE_TUTORIAL_STEPS.indexOf(this.step) + 1;
    },
    isLive() {
      return this.step === 'align' || this.step === 'check';
    },
    framed() {
      return FRAMED.includes(this.step);
    },
    canGoBack() {
      return this.branch !== '' || (this.stepIndex > 0 && this.step !== 'done');
    },
    liveUrl() {
      return this.photos[this.front];
    },
    canReplay() {
      return REPLAYABLE.includes(this.step);
    },
    // The live view paints the flip of Modo Periscopio™ even before it is on, so the photo reads the way
    // it will be saved. The camera reads it once, at start: it must be in place before it mounts.
    cameraSettings() {
      return { ...this.settings, flipVertically: true };
    },
    photoFlip() {
      return { transform: this.settings.flipHorizontally ? 'scaleY(-1) scaleX(-1)' : 'scaleY(-1)' };
    },
    sceneStyle() {
      return { width: px(STAGE.width), height: px(STAGE.height), transform: this.sceneTransform };
    },
    // The corner scenes: the periscope's centre lands on the scene's `center`, the group scaled.
    frameStyle() {
      // The branches are framed like the stick step: the same corner, the same periscope.
      const f = FRAMING[this.step] || (BRANCH_PARENT[this.step] ? FRAMING.stick : null);
      if (!f) return {};
      const [px0, py0] = MOTION.periscopeCenter;
      return { transform: `translate(${f.center[0] - f.scale * px0}px, ${f.center[1] - f.scale * py0}px) scale(${f.scale})` };
    },
    // The timings of the flip, and its frames each with the window (start, length, in ms of the run) it is shown in.
    flipVars() {
      return { '--pt-flip-cycle': `${FLIP_CYCLE_MS}ms` };
    },
    flipFrames() {
      const ms = (pct) => Math.round((pct * FLIP_CYCLE_MS) / 100);
      const n = FLIP_FRAMES.length;
      const slot = (FLIP.turnEnd - FLIP.turnStart) / n;
      const frames = [];
      // Turning over: frame 0 is also the resting and the lifting one; the last one waits for the strip.
      FLIP_FRAMES.forEach((f, k) => {
        const from = k === 0 ? 0 : FLIP.turnStart + k * slot;
        const to = k === n - 1 ? FLIP.swap : FLIP.turnStart + (k + 1) * slot;
        frames.push({ id: `flip-${k}`, src: f.src, x: f.x, y: f.y, w: f.w, h: f.h, at: ms(from), dur: ms(to) - ms(from) });
      });
      // Turning back, with the tape on: the last pose first, the first one also coming down.
      FLIP_FRAMES_TAPED.forEach((f, k) => {
        const j = n - 1 - k;
        const from = FLIP.swap + j * slot;
        const to = j === n - 1 ? FLIP.handover : FLIP.swap + (j + 1) * slot;
        frames.push({ id: `tape-${k}`, src: f.src, x: f.x, y: f.y, w: f.w, h: f.h, at: ms(from), dur: ms(to) - ms(from) });
      });
      return frames;
    },
    // The timings of the turn, for the keyframes and the frame-by-frame delays below.
    turnVars() {
      return {
        '--pt-turn': `${TURN_MS}ms`,
        '--pt-turn-cycle': `${TURN_CYCLE_MS}ms`,
        '--pt-turn-n': TURN_FRAMES.length,
      };
    },
    pressAt() {
      return { left: px(MOTION.press[0]), top: px(MOTION.press[1]) };
    },
    examples() {
      const list = [];
      if (EXAMPLES.good) list.push({ id: 'good', src: EXAMPLES.good, ok: true, label: 'message.periscopeTutorialExampleGood' });
      list.push({ id: 'bad-bottom', src: EXAMPLES.badBottom, ok: false, band: 'bottom', label: 'message.periscopeTutorialExampleBadBottom' });
      list.push({ id: 'bad-top', src: EXAMPLES.badTop, ok: false, band: 'top', label: 'message.periscopeTutorialExampleBadTop' });
      return list;
    },
    // The inset keeps the composition of the scene (it scales with it) but never reads below 14 px.
    insetStyle() {
      const k = Math.min(Math.max(this.stageScale, 0.8), 1.15);
      return { transform: `scale(${k})` };
    },
    sectionStyle() {
      return { transform: `translate(${SECTION.offset[0]}px, ${SECTION.offset[1]}px) scale(${SECTION.scale})` };
    },
    rayPath() {
      return SECTION.ray.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ');
    },
    chips() {
      const at = ([x, y]) => ({ x: x * SECTION.scale + SECTION.offset[0], y: y * SECTION.scale + SECTION.offset[1] });
      return [
        { id: 'camera', label: 'message.periscopeTutorialPlaceCamera', ...at(SECTION.labels.camera) },
        { id: 'mirror', label: 'message.periscopeTutorialPlaceMirror', ...at(SECTION.labels.mirror) },
        { id: 'table', label: 'message.periscopeTutorialPlaceTable', ...at(SECTION.labels.table) },
      ];
    },
  },
  watch: {
    isLive(now) {
      if (now) this.beginLive();
      else this.endLive();
    },
  },
  created() {
    // Not reactive on purpose: the loop's own bookkeeping.
    this._liveToken = 0;
    this._runningToken = -1;
    this._photoWaiter = null;
    this._startTimer = null;
    this._lastSource = null;
    this._flipApplied = false;
    this._gone = false;
  },
  mounted() {
    this.measureStage();
    if (typeof ResizeObserver !== 'undefined') {
      this._observer = new ResizeObserver(() => this.measureStage());
      this._observer.observe(this.$refs.stage);
    } else {
      window.addEventListener('resize', this.measureStage);
    }
    // From Settings there is no welcome to press "Empezar" on: opening it is starting.
    if (this.periscopeSource !== 'first_login') this.track('started');
    this.trackStep();
    if (this.isLive) this.beginLive();
  },
  beforeUnmount() {
    this._gone = true;
    this.endLive();
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('resize', this.measureStage);
  },
  methods: {
    boxOf(l) {
      return { left: px(l.x), top: px(l.y), width: px(l.w), height: px(l.h) };
    },
    box(key) {
      return this.boxOf(LAYERS[key]);
    },
    track(event, props = {}) {
      trackPeriscope(event, { ...props, source: this.periscopeSource });
    },
    /** `index` is the step's "Paso n" (1-7), 0 for the welcome, 8 for the end and, on a branch, the index of
     *  the step it hangs off. */
    trackStep() {
      const n = this.stepNumber;
      let index = n || (this.step === 'welcome' ? 0 : TOTAL_STEPS + 1);
      if (BRANCH_PARENT[this.step]) index = PERISCOPE_TUTORIAL_STEPS.indexOf(BRANCH_PARENT[this.step]) + 1;
      this.track('step', { step: this.step, index });
    },

    // ---- the stage ----
    measureStage() {
      const el = this.$refs.stage;
      if (!el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      // Cover, centred: the corner scenes keep what matters at the middle, so what a narrow box crops is air.
      const s = Math.max(w / STAGE.width, h / STAGE.height);
      this.stageScale = s;
      this.sceneTransform = `translate(${(w - STAGE.width * s) / 2}px, ${(h - STAGE.height * s) / 2}px) scale(${s})`;
    },

    // ---- the flow ----
    goTo(index) {
      this.stepIndex = index;
      this.branch = '';
      this.replayKey += 1;
      if (this.step === 'done' && !this._flipApplied) {
        // The guide ends with the mirror in place: the image comes out upside down, so turn the fix on.
        this._flipApplied = true;
        this.$emit('update-settings', { ...this.settings, flipVertically: true });
      }
      this.trackStep();
    },
    goToBranch(id) {
      this.branch = id;
      this.replayKey += 1;
      this.trackStep();
    },
    next() {
      if (this.stepIndex < this.steps.length - 1) this.goTo(this.stepIndex + 1);
    },
    back() {
      if (!this.canGoBack) return;
      // From a branch, back is the step it hangs off (stepIndex never moved).
      this.goTo(this.branch ? this.stepIndex : this.stepIndex - 1);
    },
    replay() {
      this.replayKey += 1;
    },
    onPrimary() {
      if (this.step === 'welcome') {
        this.track('started');
        this.next();
      } else if (this.step === 'done') {
        this.reattach = false;
        this.track('completed', { capture_source: this._lastSource || null });
        this.$emit('periscope-close', { outcome: PERISCOPE_OUTCOME.COMPLETED, step: 'done' });
      } else if (this.step === UNSTICK) {
        // Unstuck: align again, and the next stick is putting it back, not peeling a plastic.
        this.reattach = true;
        this.goTo(this.steps.indexOf('align'));
      } else if (this.step === RETAPE) {
        this.goTo(this.stepIndex); // back to stick, still a re-attach
      } else {
        this.next();
      }
    },
    onSecondary() {
      if (this.step === 'welcome') {
        this.track('already_installed');
        this.$emit('periscope-close', { outcome: PERISCOPE_OUTCOME.ALREADY_INSTALLED, step: 'welcome' });
      } else if (this.step === 'check') {
        // Not right: first get it unstuck, then align again.
        this.goToBranch(UNSTICK);
      } else if (this.step === 'stick' && this.reattach) {
        // It does not hold: new double-sided tape.
        this.goToBranch(RETAPE);
      }
    },
    exit() {
      this.reattach = false;
      this.track('dismissed', { step: this.step });
      this.$emit('periscope-close', { outcome: PERISCOPE_OUTCOME.DISMISSED, step: this.step });
    },

    // ---- the live view ----
    // One loop at a time, tied to a token: every start of the live view takes a new one, and a loop whose
    // token is not the current one stops at its next look.
    beginLive() {
      this._liveToken += 1;
      this.liveState = 'starting';
      this.liveSource = '';
      clearTimeout(this._startTimer);
      this._startTimer = setTimeout(() => {
        if (this.liveState === 'starting') this.liveState = 'error';
      }, LIVE_START_LIMIT_MS);
    },
    endLive() {
      this._liveToken += 1;
      clearTimeout(this._startTimer);
      this.clearPhoto();
    },
    retryLive() {
      this.endLive();
      this.cameraKey += 1;
      this.beginLive();
    },
    onCameraState(state) {
      if (!this.isLive || this._gone || !state) return;
      if (state.fault) {
        this.liveState = 'error';
      } else if (state.ready) {
        this.runLive();
      }
    },
    async runLive() {
      const token = this._liveToken;
      if (this._runningToken === token) return;
      this._runningToken = token;
      let failures = 0;
      while (token === this._liveToken && !this._gone) {
        const camera = this.$refs.camera;
        if (!camera) break;
        let result;
        try {
          result = await camera.captureFullFrame();
          failures = 0;
        } catch (error) {
          failures += 1;
          if (token !== this._liveToken) break;
          if (failures >= LIVE_MAX_FAILURES) {
            this.liveState = 'error';
            break;
          }
          await wait(LIVE_RETRY_MS);
          continue;
        }
        if (token !== this._liveToken) break;
        this.liveSource = result.source;
        this._lastSource = result.source;
        if (result.source === 'canvas') {
          // takePhoto is not usable here: the component's own video is already the full frame, flipped.
          this.clearPhoto();
          this.liveState = 'live';
          break;
        }
        const shown = await this.showPhoto(result.blob);
        if (token !== this._liveToken) break;
        this.liveState = 'live';
        // A photo that would not load is skipped; a beat before the next so a bad run is not a tight loop.
        if (!shown) await wait(LIVE_RETRY_MS);
      }
    },
    /**
     * Puts a photo on screen; resolves (true) once it has been swapped in, which is when the next one may be
     * taken, or (false) when it could not be shown. The photo goes into the buffer that is not in front, and
     * `onPhotoLoad` brings it forward only when it is loaded and decoded: until then the previous photo stays.
     */
    showPhoto(blob) {
      return new Promise((resolve) => {
        this.settlePhoto(false);
        this._photoWaiter = resolve;
        this.photos[1 - this.front] = URL.createObjectURL(blob);
      });
    },
    settlePhoto(ok) {
      const waiter = this._photoWaiter;
      this._photoWaiter = null;
      if (waiter) waiter(ok);
    },
    async onPhotoLoad(event, i) {
      const url = this.photos[i];
      if (!url || i === this.front) return;
      // Decoded before it is shown, so the first paint of it is the photo and not a blank.
      if (typeof event.target.decode === 'function') {
        try { await event.target.decode(); } catch { /* shown anyway: it loaded */ }
      }
      if (this.photos[i] !== url) return; // the live view ended meanwhile; clearPhoto settled it
      const old = this.front;
      const oldUrl = this.photos[old];
      this.front = i;
      this.photos[old] = '';
      // The photo it replaced can go now that the new one is up.
      if (oldUrl) URL.revokeObjectURL(oldUrl);
      this.settlePhoto(true);
    },
    onPhotoError(i) {
      const url = this.photos[i];
      if (!url || i === this.front) return;
      this.photos[i] = '';
      URL.revokeObjectURL(url);
      this.settlePhoto(false);
    },
    clearPhoto() {
      const urls = this.photos.slice();
      this.photos = ['', ''];
      this.front = 0;
      urls.forEach((url) => { if (url) URL.revokeObjectURL(url); });
      this.settlePhoto(false);
    },
  },
};
</script>

<style scoped>
/* On the design system: only its tokens, the buttons of src/ui/components/ and its spinner. A full screen
   like BzScreen (bar, then content) whose content is two columns: a stage and the words of one step. */
.pt {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  font-synthesis: none;
  font-kerning: normal;
  isolation: isolate;
}

/* ---- the bar: leave, the name, how far along ---- */
.pt-bar {
  flex: none;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--bz-space-4);
  box-sizing: border-box;
  min-height: var(--bz-bar-height);
  padding: env(safe-area-inset-top) max(var(--bz-space-6), env(safe-area-inset-right)) 0 max(var(--bz-space-6), env(safe-area-inset-left));
  border-bottom: 1px solid var(--bz-edge);
}

.pt-exit {
  justify-self: start;
}

.pt-bar-title {
  font-size: var(--bz-text-lg);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  text-align: center;
}

.pt-progress {
  grid-column: 3;
  justify-self: end;
  display: flex;
  align-items: center;
  gap: var(--bz-space-3);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  white-space: nowrap;
}

.pt-dots {
  display: flex;
  gap: var(--bz-space-2);
}

.pt-dot {
  width: 10px;
  height: 10px;
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
}

.pt-dot.is-done {
  background: var(--bz-action-ink);
}

/* ---- the content: stage and copy ---- */
.pt-main {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 58fr) minmax(0, 42fr);
  gap: var(--bz-space-7);
  box-sizing: border-box;
  padding: var(--bz-space-6) max(var(--bz-space-6), env(safe-area-inset-right)) max(var(--bz-space-6), env(safe-area-inset-bottom)) max(var(--bz-space-6), env(safe-area-inset-left));
}

.pt-stage {
  position: relative;
  min-height: 0;
  overflow: hidden;
  border-radius: var(--bz-radius-xl);
  background: var(--bz-surface);
}

.pt-copy {
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

/* `margin-block: auto` centres the copy while it fits and lets it scroll from its top when it does not. */
.pt-copy-inner {
  margin-block: auto;
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-4);
}

.pt-title {
  margin: 0;
  font-size: var(--bz-text-3xl);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  color: var(--bz-ink);
  text-wrap: balance;
}

.pt-text {
  margin: 0;
  font-size: var(--bz-text-xl);
  font-weight: var(--bz-weight-body);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  color: var(--bz-ink-2);
  text-wrap: pretty;
}

/* The sentence to keep, apart from the rest and in the first ink. */
.pt-text.pt-strong {
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
}

.pt-note {
  margin: 0;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  color: var(--bz-ink-2);
}

.pt-actions {
  display: flex;
  flex-direction: column;
  gap: var(--bz-target-gap);
  margin-top: var(--bz-space-4);
}

.pt-aux {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--bz-space-2);
}

/* ---- the stage: what every scene stands on ---- */
.pt-holder {
  position: absolute;
  inset: 0;
}

.pt-scene {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
}

.pt-frame {
  position: absolute;
  inset: 0;
  transform-origin: 0 0;
}

.pt-layer {
  position: absolute;
  max-width: none;
  user-select: none;
  pointer-events: none;
}

.pt-ghost {
  opacity: 0.28;
}

/* ---- needs: four things on four cards ---- */
.pt-needs {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: var(--bz-space-4);
  padding: var(--bz-space-5);
}

.pt-need {
  margin: 0;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--bz-space-3);
  box-sizing: border-box;
  padding: var(--bz-space-4);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
}

.pt-need-img {
  flex: 1;
  min-height: 0;
  width: 100%;
  object-fit: contain;
}

.pt-need-caption {
  font-size: var(--bz-text-lg);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  text-align: center;
}

/* ---- clean: sparkles ---- */
.pt-spark {
  position: absolute;
  width: 34px;
  height: 34px;
}

.pt-spark path {
  fill: var(--bz-ink);
}

/* ---- stick: where to push ---- */
.pt-ripple {
  position: absolute;
  width: 64px;
  height: 64px;
  margin: -32px 0 0 -32px;
  box-sizing: border-box;
  border: 4px solid var(--bz-action-ink);
  border-radius: var(--bz-radius-full);
  opacity: 0;
}

/* The press cue: a round badge with a hand that comes down on the spot and presses twice. */
.pt-tap {
  position: absolute;
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  margin: -28px 0 0 -28px;
  box-sizing: border-box;
  border: 3px solid var(--bz-bg);
  border-radius: var(--bz-radius-full);
  background: var(--bz-action);
  color: var(--bz-on-action);
  opacity: 0;
}

.pt-tap-icon {
  width: var(--bz-icon-lg);
  height: var(--bz-icon-lg);
}

/* The pieces that are not there once the step is done. */
.pt-liner,
.pt-pencil {
  opacity: 0;
}

/* ---- unstick: the periscope is already lifted at rest (the finished pose); the shadow has gone with it.
   Its frames sit one over another and are shown one at a time (see the motion below). ---- */
.pt-turn {
  position: absolute;
  inset: 0;
  transform: translate(var(--pt-lift30));
}

.pt-turn-frame {
  opacity: 0;
}

.pt-turn-frame.is-first {
  opacity: 1;
}

.pt-shade-unstick {
  opacity: 0;
}

/* ---- retape: drawn at rest (the periscope down on its new tape, as in the end of the step); the motion
   below hides it, plays the flip and gives it back ---- */
.pt-flip {
  position: absolute;
  inset: 0;
}

.pt-flip-frame,
.pt-flip-strip {
  opacity: 0;
}

/* ---- done: the check ---- */
.pt-ok-badge {
  position: absolute;
  top: var(--bz-space-6);
  right: var(--bz-space-6);
  display: grid;
  place-items: center;
  width: var(--bz-target-hero);
  height: var(--bz-target-hero);
  border-radius: var(--bz-radius-full);
  background: var(--bz-ok);
  color: var(--bz-on-ok-solid);
}

.pt-ok-icon {
  width: calc(var(--bz-icon-lg) * 2);
  height: calc(var(--bz-icon-lg) * 2);
}

/* ---- place: the side view in a card, over the corner of the stage ---- */
.pt-inset {
  position: absolute;
  right: var(--bz-space-4);
  bottom: var(--bz-space-4);
  width: 330px;
  box-sizing: border-box;
  padding: var(--bz-space-3);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
  transform-origin: 100% 100%;
}

.pt-inset-art {
  position: relative;
  width: 100%;
  aspect-ratio: 310 / 268;
}

.pt-section {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: var(--bz-radius-md);
  background: var(--bz-surface);
}

.pt-section-scene {
  position: absolute;
  left: 0;
  top: 0;
  width: 760px;
  height: 640px;
  transform-origin: 0 0;
}

.pt-ray {
  position: absolute;
  left: 0;
  top: 0;
}

.pt-ray path {
  fill: none;
  stroke: var(--bz-action-ink);
  stroke-width: 6;
  stroke-dasharray: 14 10;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* The chips sit over the section and may reach past it into the card's padding. */
.pt-chip {
  position: absolute;
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
  padding: var(--bz-space-1) var(--bz-space-3) var(--bz-space-1) var(--bz-space-1);
  border-radius: var(--bz-radius-full);
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  white-space: nowrap;
}

.pt-chip-n {
  display: inline-grid;
  place-items: center;
  width: var(--bz-space-6);
  height: var(--bz-space-6);
  border-radius: var(--bz-radius-full);
  background: var(--bz-action-ink);
  color: var(--bz-bg);
  font-size: var(--bz-text-sm);
  font-weight: var(--bz-weight-strong);
  line-height: var(--bz-leading-sm);
}

.pt-inset-caption {
  margin: var(--bz-space-2) var(--bz-space-1) var(--bz-space-1);
  font-size: var(--bz-text-lg);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
}

/* ---- align and check: the live view, with the examples under it ---- */
.pt-live-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-3);
  padding: var(--bz-space-3);
}

.pt-live {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: var(--bz-radius-lg);
  background: var(--bz-bg);
}

/* The component sizes itself to the screen it was built for; here it is the box of the view. */
.pt-camera {
  position: absolute;
  inset: 0;
  height: 100% !important;
}

/* The component says its own states in its own words: this screen says them (below). */
.pt-live :deep(.camera-message) {
  display: none;
}

/* While a photo is up, the video keeps playing under it, unseen. */
.pt-live.has-photo :deep(.preview-video) {
  visibility: hidden;
}

.pt-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

/* The photo in front; the one behind (loading, or the one that was up) is under it. */
.pt-photo.is-front {
  z-index: 1;
}

.pt-live-badge {
  z-index: 2;
  position: absolute;
  top: var(--bz-space-3);
  left: var(--bz-space-3);
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
  padding: var(--bz-space-2) var(--bz-space-3);
  border-radius: var(--bz-radius-full);
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-size: var(--bz-text-md);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-md);
}

.pt-live-dot {
  width: 10px;
  height: 10px;
  border-radius: var(--bz-radius-full);
  background: var(--bz-ok);
}

.pt-live-msg {
  z-index: 2;
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-4);
  padding: var(--bz-space-4);
  text-align: center;
  background: var(--bz-bg);
}

.pt-live-icon {
  font-size: var(--bz-icon-lg);
  color: var(--bz-ink-2);
}

.pt-live-text {
  margin: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  color: var(--bz-ink);
}

/* Small next to the camera: they show what to avoid at a glance, the room is for the live photo. */
.pt-examples {
  flex: none;
  align-self: center;
  width: 60%;
  display: grid;
  grid-template-columns: repeat(var(--pt-examples), minmax(0, 1fr));
  gap: var(--bz-space-3);
  margin: 0;
  padding: 0;
  list-style: none;
}

.pt-example {
  min-width: 0;
}

.pt-example-pic {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: var(--bz-radius-md);
  background: var(--bz-bg);
}

.pt-example-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* The strip where the other scene gets mixed in. */
.pt-band {
  position: absolute;
  left: 0;
  right: 0;
  height: 30%;
  box-sizing: border-box;
  border: 3px solid var(--bz-wrong);
}

/* The outline follows the rounded corners of the picture it touches (the same radius as its box). */
.pt-band--bottom {
  bottom: 0;
  border-radius: 0 0 var(--bz-radius-md) var(--bz-radius-md);
}

.pt-band--top {
  top: 0;
  border-radius: var(--bz-radius-md) var(--bz-radius-md) 0 0;
}

.pt-example-caption {
  display: flex;
  align-items: center;
  gap: var(--bz-space-1);
  margin: var(--bz-space-1) 0 0;
  font-size: var(--bz-text-sm);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-sm);
  color: var(--bz-ink);
}

.pt-example-icon.is-ok { color: var(--bz-ok); }
.pt-example-icon.is-wrong { color: var(--bz-wrong); }

/* ---- motion: opacity and transform only, three runs, and the last frame is the finished pose ---- */
@keyframes pt-drop {
  0% { transform: translate(var(--pt-lift40)); opacity: 0; }
  12% { opacity: 1; }
  45%, 100% { transform: translate(0, 0); opacity: 1; }
}

@keyframes pt-shadedrop {
  0%, 20% { opacity: 0; }
  45%, 100% { opacity: 1; }
}

@keyframes pt-wipe {
  0% { transform: translate(var(--pt-wipe-0)); }
  25% { transform: translate(var(--pt-wipe-1)); }
  50% { transform: translate(var(--pt-wipe-2)); }
  75% { transform: translate(var(--pt-wipe-3)); }
  100% { transform: translate(var(--pt-wipe-0)); }
}

@keyframes pt-spark {
  0%, 55% { opacity: 0; transform: scale(0.3); }
  70% { opacity: 1; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes pt-xray {
  0%, 4% { opacity: 1; }
  9%, 70% { opacity: 0.38; }
  80%, 100% { opacity: 1; }
}

@keyframes pt-seg {
  0%, 12% { opacity: 0; }
  13%, 100% { opacity: 1; }
}

@keyframes pt-pencil {
  0% { transform: translate(0, 0); opacity: 1; }
  12.5% { transform: translate(var(--pt-pencil-1)); }
  25% { transform: translate(var(--pt-pencil-2)); }
  37.5%, 50% { transform: translate(var(--pt-pencil-3)); opacity: 1; }
  62%, 100% { transform: translate(var(--pt-pencil-3)); opacity: 0; }
}

@keyframes pt-peel {
  0%, 14% { transform: translate(var(--pt-lift30)); opacity: 1; }
  34%, 100% { transform: translate(var(--pt-peel)) rotate(-10deg); opacity: 0; }
}

@keyframes pt-press {
  0%, 16% { transform: translate(var(--pt-lift30)); }
  48%, 100% { transform: translate(0, 0); }
}

@keyframes pt-shadepress {
  0%, 30% { opacity: 0; }
  48%, 100% { opacity: 1; }
}

@keyframes pt-tap {
  0%, 50% { opacity: 0; transform: translateY(-34px) scale(1); }
  56% { opacity: 1; transform: translateY(-12px) scale(1); }
  61% { opacity: 1; transform: translateY(0) scale(0.86); }
  67% { opacity: 1; transform: translateY(-10px) scale(1); }
  73% { opacity: 1; transform: translateY(0) scale(0.86); }
  79% { opacity: 1; transform: translateY(-10px) scale(1); }
  90%, 100% { opacity: 0; transform: translateY(-10px) scale(1); }
}

@keyframes pt-ripple {
  0%, 61% { opacity: 0; transform: scale(0.4); }
  63% { opacity: 1; }
  72% { opacity: 0; transform: scale(1.7); }
  73% { opacity: 0; transform: scale(0.4); }
  75% { opacity: 1; }
  84%, 100% { opacity: 0; transform: scale(1.7); }
}

/* One run of the unstick scene is 4.2 s: the turn takes the first 2.4 s (57.1 %), then the periscope lifts and
   the shadow goes. Same numbers as TURN_MS / TURN_CYCLE_MS in the script. */
@keyframes pt-unstick-lift {
  0%, 57.1% { transform: translate(0, 0); }
  83%, 100% { transform: translate(var(--pt-lift30)); }
}

@keyframes pt-unstick-shade {
  0%, 57.1% { opacity: 1; }
  83%, 100% { opacity: 0; }
}

/* Re-taping, one run of 8 s (the beats are FLIP in the script): the periscope lifts off the tablet (6-16 %) and
   comes down again (80-90 %); the strip flies from the roll to its place on the upturned base (40-55 %) and
   is replaced by the taped frames at 58 %; the finished pose is hidden until the hand-over at 90 %. */
@keyframes pt-flip-move {
  0%, 6% { transform: translate(var(--pt-flip-drop)); animation-timing-function: ease-in-out; }
  16%, 80% { transform: translate(0, 0); animation-timing-function: ease-in-out; }
  90%, 100% { transform: translate(var(--pt-flip-drop)); }
}

@keyframes pt-flip-strip {
  0%, 38% { opacity: 0; transform: translate(var(--pt-flip-strip-from)); }
  40% { opacity: 0.6; transform: translate(var(--pt-flip-strip-from)); animation-timing-function: ease-in-out; }
  55%, 57.9% { opacity: 1; transform: translate(0, 0); }
  58%, 100% { opacity: 0; transform: translate(0, 0); }
}

@keyframes pt-retape-rest {
  0%, 89.9% { opacity: 0; }
  90%, 100% { opacity: 1; }
}

/* A frame of the turn is shown for its slot of the turn (1/N of 2.4 s), once per run; the first one, which is
   also the resting one, is hidden for the rest of the turn. */
@keyframes pt-turn-show {
  from, to { opacity: 1; }
}

@keyframes pt-turn-hide {
  from, to { opacity: 0; }
}

/* Every move is in here, so with reduced motion each scene is drawn finished and still. */
@media (prefers-reduced-motion: no-preference) {
  .pt-drop { animation: pt-drop 4.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0s 3 both; }
  .pt-shade-drop { animation: pt-shadedrop 4.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0s 3 both; }
  .pt-cloth { animation: pt-wipe 2.4s ease-in-out 0s 3 both; }
  .pt-spark { animation: pt-spark 2.4s ease-out 0s 3 both; }
  .pt-spark--1 { animation-delay: 0.5s; }
  .pt-spark--2 { animation-delay: 1s; }
  .pt-xray { animation: pt-xray 6s ease-in-out 0s 3 both; }
  .pt-mark { animation: pt-seg 6s linear 0s 3 both; }
  .pt-mark--1 { animation-delay: 0.75s; }
  .pt-mark--2 { animation-delay: 1.5s; }
  .pt-pencil { animation: pt-pencil 6s linear 0s 3 both; }
  .pt-liner { animation: pt-peel 6s ease-in 0s 3 both; }
  .pt-press { animation: pt-press 6s cubic-bezier(0.2, 0.8, 0.2, 1) 0s 3 both; }
  .pt-shade-press { animation: pt-shadepress 6s cubic-bezier(0.2, 0.8, 0.2, 1) 0s 3 both; }
  .pt-tap { animation: pt-tap 6s ease-in-out 0s 3 both; }
  .pt-ripple { animation: pt-ripple 6s ease-out 0s 3 both; }

  .pt-flip { animation: pt-flip-move 8s linear 0s 3 both; }
  .pt-flip-strip { animation: pt-flip-strip 8s linear 0s 3 both; }
  .pt-retape-rest { animation: pt-retape-rest 8s linear 0s 3 both; }

  /* A frame is shown for its window of the run (--pt-a from the start, --pt-d long), once per run: three
     animations, each with its own delay. */
  .pt-flip-frame {
    animation-name: pt-turn-show, pt-turn-show, pt-turn-show;
    animation-duration: var(--pt-d);
    animation-timing-function: linear;
    animation-fill-mode: none;
    animation-delay:
      var(--pt-a),
      calc(var(--pt-a) + var(--pt-flip-cycle)),
      calc(var(--pt-a) + 2 * var(--pt-flip-cycle));
  }

  .pt-turn { animation: pt-unstick-lift var(--pt-turn-cycle) cubic-bezier(0.2, 0.8, 0.2, 1) 0s 3 both; }
  .pt-shade-unstick { animation: pt-unstick-shade var(--pt-turn-cycle) ease-in-out 0s 3 both; }

  /* One animation per run (three of them), each with its own delay: frame i starts at i/N of the turn. */
  .pt-turn-frame {
    animation-name: pt-turn-show, pt-turn-show, pt-turn-show;
    animation-duration: calc(var(--pt-turn) / var(--pt-turn-n));
    animation-timing-function: linear;
    animation-fill-mode: none;
    animation-delay:
      calc(var(--pt-i) * var(--pt-turn) / var(--pt-turn-n)),
      calc(var(--pt-i) * var(--pt-turn) / var(--pt-turn-n) + var(--pt-turn-cycle)),
      calc(var(--pt-i) * var(--pt-turn) / var(--pt-turn-n) + 2 * var(--pt-turn-cycle));
  }

  .pt-turn-frame.is-first {
    animation-name: pt-turn-hide, pt-turn-hide, pt-turn-hide;
    animation-duration: calc(var(--pt-turn) * (var(--pt-turn-n) - 1) / var(--pt-turn-n));
    animation-delay:
      calc(var(--pt-turn) / var(--pt-turn-n)),
      calc(var(--pt-turn) / var(--pt-turn-n) + var(--pt-turn-cycle)),
      calc(var(--pt-turn) / var(--pt-turn-n) + 2 * var(--pt-turn-cycle));
  }

  /* A step comes in with a short fade and a nudge, as the help flow's does. */
  .pt-step-enter-active,
  .pt-step-leave-active {
    transition: opacity 80ms ease-out, transform 80ms ease-out;
  }

  .pt-step-enter-from { transform: translateX(10px); }
  .pt-step-leave-to { transform: translateX(-10px); }
}

.pt-step-enter-from,
.pt-step-leave-to { opacity: 0; }

.pt-fade-enter-active,
.pt-fade-leave-active { transition: opacity 80ms ease-out; }

.pt-fade-enter-from,
.pt-fade-leave-to { opacity: 0; }

/* ---- responsive ---- */
/* Narrower than 900: the stage on top (up to 45% of the height), the words under it, the column scrolling. */
@media screen and (max-width: 899px) {
  .pt-main {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: none;
    grid-auto-rows: min-content;
    gap: var(--bz-space-5);
    overflow-y: auto;
    padding-top: var(--bz-space-4);
  }

  .pt-stage {
    height: 45vh;
    min-height: 240px;
  }

  .pt-copy {
    overflow-y: visible;
  }

  .pt-copy-inner {
    margin-block: 0;
  }
}

@media screen and (max-width: 639px) {
  .pt-bar { gap: var(--bz-space-3); padding-inline: max(var(--bz-space-4), env(safe-area-inset-left)) max(var(--bz-space-4), env(safe-area-inset-right)); }
  .pt-bar-title { display: none; }
  .pt-dots { display: none; }
  .pt-title { font-size: var(--bz-text-xl); line-height: var(--bz-leading-xl); letter-spacing: var(--bz-tracking-xl); }
  .pt-text { font-size: var(--bz-text-lg); line-height: var(--bz-leading-lg); letter-spacing: var(--bz-tracking-lg); }
}

/* The tablet over the pass is 614 px tall: the axis that squeezes is the height, so the air between things
   goes and the type and the targets stay. */
@media screen and (max-height: 700px) {
  .pt-main {
    gap: var(--bz-space-5);
    padding-top: var(--bz-space-4);
    padding-bottom: max(var(--bz-space-4), env(safe-area-inset-bottom));
  }

  .pt-copy-inner { gap: var(--bz-space-3); }
  .pt-actions { margin-top: var(--bz-space-2); gap: var(--bz-space-3); }
  .pt-live-wrap { gap: var(--bz-space-2); padding: var(--bz-space-2); }
  .pt-needs { gap: var(--bz-space-3); padding: var(--bz-space-4); }
  .pt-need { gap: var(--bz-space-2); padding: var(--bz-space-3); }
}
</style>
